import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import { MathOperator, ExpressionType } from '@/__generated__/graphql';

export type ExpressionNode =
  | { type: ExpressionType.Number; value: number }
  | { type: ExpressionType.Variable; variable: string }
  | { type: ExpressionType.Operation; operator: MathOperator; args: ExpressionNode[] };

const OPERATOR_OPTIONS: { value: MathOperator; label: string }[] = [
  { value: MathOperator.Add, label: 'Add' },
  { value: MathOperator.Subtract, label: 'Subtract' },
  { value: MathOperator.Divide, label: 'Divide' },
  { value: MathOperator.Multiply, label: 'Multiply' },
  { value: MathOperator.Avg, label: 'Average' },
  { value: MathOperator.Min, label: 'Min' },
  { value: MathOperator.Max, label: 'Max' },
  { value: MathOperator.Count, label: 'Count' },
  { value: MathOperator.DailyAvg, label: 'Daily Average' },
];

const EXPRESSION_TYPE_OPTIONS: { value: ExpressionType; label: string }[] = [
  { value: ExpressionType.Number, label: 'Number' },
  { value: ExpressionType.Variable, label: 'Variable' },
  { value: ExpressionType.Operation, label: 'Operation' },
];

const defaultArg = (fallbackVar?: string): ExpressionNode =>
  fallbackVar
    ? { type: ExpressionType.Variable, variable: fallbackVar }
    : { type: ExpressionType.Number, value: 0 };

export const ensureExpression = (expr: unknown, fallbackVar?: string): ExpressionNode => {
  const e = expr as Record<string, unknown>;

  if (!e || !e.type) return { type: ExpressionType.Number, value: 0 };

  if (e.type === ExpressionType.Number) {
    return { type: ExpressionType.Number, value: Number(e.value ?? 0) };
  }

  if (e.type === ExpressionType.Variable) {
    return { type: ExpressionType.Variable, variable: String(e.variable ?? fallbackVar ?? '') };
  }

  if (e.type === ExpressionType.Operation) {
    const operator =
      e.operator && Object.values(MathOperator).includes(e.operator as MathOperator)
        ? (e.operator as MathOperator)
        : MathOperator.Add;

    return {
      type: ExpressionType.Operation,
      operator,
      args: Array.isArray(e.args)
        ? e.args.map((a: unknown) => ensureExpression(a, fallbackVar))
        : [],
    };
  }

  return { type: ExpressionType.Number, value: 0 };
};

type ExpressionEditorProps = {
  value: ExpressionNode;
  disabled?: boolean;
  variableOptions: string[];
  onChange: (next: ExpressionNode) => void;
  level?: number;
};

export const ExpressionEditor = ({
  value,
  disabled = false,
  variableOptions,
  onChange,
  level = 0,
}: ExpressionEditorProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [argToDeleteIndex, setArgToDeleteIndex] = useState<number | null>(null);
  const setType = (nextType: ExpressionType) => {
    if (nextType === ExpressionType.Number) {
      onChange({ type: ExpressionType.Number, value: 0 });
    } else if (nextType === ExpressionType.Variable) {
      onChange({ type: ExpressionType.Variable, variable: variableOptions[0] || '' });
    } else if (nextType === ExpressionType.Operation) {
      onChange({
        type: ExpressionType.Operation,
        operator: MathOperator.Add,
        args: [defaultArg(variableOptions[0])],
      });
    }
  };

  const header = (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
      <TextField
        select
        size="small"
        label="Type"
        value={value.type}
        disabled={disabled}
        onChange={(e) => setType(e.target.value as ExpressionType)}
        sx={{ minWidth: 170, alignSelf: 'flex-start' }}
      >
        {EXPRESSION_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {value.type === ExpressionType.Number && (
        <TextField
          size="small"
          type="number"
          label="Value"
          value={value.value}
          disabled={disabled}
          onChange={(e) => {
            const n = Number.parseFloat(e.target.value);
            onChange({ type: ExpressionType.Number, value: Number.isNaN(n) ? 0 : n });
          }}
          sx={{ minWidth: 220, alignSelf: 'flex-start' }}
        />
      )}

      {value.type === ExpressionType.Variable && (
        <TextField
          select
          size="small"
          label="Variable"
          value={value.variable}
          disabled={disabled}
          onChange={(e) => onChange({ type: ExpressionType.Variable, variable: e.target.value })}
          sx={{ minWidth: 260, alignSelf: 'flex-start' }}
          helperText={!variableOptions.includes(value.variable) ? 'Not in variables list' : ' '}
        >
          {variableOptions.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </TextField>
      )}

      {value.type === ExpressionType.Operation && (
        <TextField
          select
          size="small"
          label="Operation"
          value={value.operator}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...value,
              operator: e.target.value as MathOperator,
            })
          }
          sx={{ minWidth: 220, alignSelf: 'flex-start' }}
        >
          {OPERATOR_OPTIONS.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );

  if (value.type !== ExpressionType.Operation) {
    return <Box>{header}</Box>;
  }

  const args = value.args || [];

  return (
    <>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          ml: level * 2,
          backgroundColor: level > 0 ? 'grey.50' : 'background.paper',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Type"
            value={value.type}
            onChange={(e) => setType(e.target.value as ExpressionType)}
            disabled={disabled}
            sx={{ minWidth: 120 }}
          >
            {EXPRESSION_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Stack spacing={2}>
            {value.type === ExpressionType.Operation && (
              <Stack spacing={2}>
                <TextField
                  select
                  size="small"
                  label="Operator"
                  value={value.operator}
                  onChange={(e) => onChange({ ...value, operator: e.target.value as MathOperator })}
                  disabled={disabled}
                  sx={{ minWidth: 120 }}
                >
                  {OPERATOR_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Arguments ({args.length})</Typography>
                    {!disabled && (
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() => {
                          const nextArgs = [...args, defaultArg(variableOptions[0])];
                          onChange({ ...value, args: nextArgs });
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </Stack>

                  {args.length > 0 && (
                    <Stack spacing={2}>
                      {args.map((arg, i) => (
                        <Box key={i}>
                          <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Box flex={1}>
                              <ExpressionEditor
                                value={arg}
                                disabled={disabled}
                                variableOptions={variableOptions}
                                level={level + 1}
                                onChange={(nextArg) => {
                                  const nextArgs = args.map((a, idx) => (idx === i ? nextArg : a));
                                  onChange({ ...value, args: nextArgs });
                                }}
                              />
                            </Box>

                            {!disabled && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setArgToDeleteIndex(i);
                                  setIsDeleteDialogOpen(true);
                                }}
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Stack>

                          {i < args.length - 1 && <Divider sx={{ mt: 2 }} />}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>

      {isDeleteDialogOpen && (
        <GenericDialog
          open={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setArgToDeleteIndex(null);
          }}
          title="Delete Expression Argument"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this expression argument?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setArgToDeleteIndex(null);
                }}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (argToDeleteIndex !== null && value.type === ExpressionType.Operation) {
                    const nextArgs = value.args.filter((_, idx) => idx !== argToDeleteIndex);
                    onChange({ ...value, args: nextArgs });
                  }
                  setIsDeleteDialogOpen(false);
                  setArgToDeleteIndex(null);
                }}
                sx={containedButtonStyle}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </>
  );
};
